import { get } from 'lodash';
import { ParsedCard, ParsedField, ParsedHouseConsignment, ParsedItem } from 'store/declaration-form-errors/ParsedForm';
import addPathPrefix from 'utils/addPathPrefix';
import { handleField, parseFieldErrorsForCard, parseFieldErrorsForMultipleCard } from 'utils/validationParserUtils';

/**
 * `partyPath` is where the party sits in the form being parsed. A listed error takes the reader to
 * its field by the field's path, and a path that stops at `address.street` is every party's street
 * at once - the form can neither open the right tab nor find the right input - so every path
 * recorded here starts at the party.
 */
export const parsePartyFields = (party: any, label: string, fields: ParsedField[], partyPath: string) => {
    if (typeof party === 'string') {
        fields.push({ name: label, message: party, path: partyPath });
        return;
    }
    if (!party) return;

    const named = (name: string) => `${label} - ${name}`;

    /** `handleField` records the path it read the message at, which is relative to `item`. */
    const collectUnder = (itemPath: string, collect: () => void) => {
        const firstNew = fields.length;

        collect();

        fields.slice(firstNew).forEach((field) => {
            field.path = addPathPrefix(itemPath, field.path);
        });
    };

    collectUnder(partyPath, () => {
        handleField(fields, party, 'identificationNumber', named('Identification Number'));
        handleField(fields, party, 'name', named('Name'));
        handleField(fields, party, 'typeOfPerson', named('Type of Person'));
        handleField(fields, party, 'status', named('Status'));
        handleField(fields, party, 'address.street', named('Street'));
        handleField(fields, party, 'address.streetAdditionalLine', named('Street Additional Line'));
        handleField(fields, party, 'address.number', named('Number'));
        handleField(fields, party, 'address.poBox', named('P.O. Box'));
        handleField(fields, party, 'address.city', named('City'));
        handleField(fields, party, 'address.subDivision', named('Sub-division'));
        handleField(fields, party, 'address.postCode', named('Post Code'));
        handleField(fields, party, 'address.country', named('Country'));
    });

    const communication = get(party, 'communication');
    const communicationPath = addPathPrefix(partyPath, 'communication');

    if (typeof communication === 'string') {
        fields.push({ name: named('Communication'), message: communication, path: communicationPath });
    } else if (Array.isArray(communication)) {
        communication.forEach((entry, index) => {
            const entryName = communication.length > 1 ? `Communication ${index + 1}` : 'Communication';

            collectUnder(addPathPrefix(communicationPath, String(index)), () => {
                handleField(fields, entry, 'identifier', named(`${entryName} - Identifier`));
                handleField(fields, entry, 'type', named(`${entryName} - Type`));
            });
        });
    } else if (communication) {
        collectUnder(communicationPath, () => {
            handleField(fields, communication, 'identifier', named('Communication - Identifier'));
            handleField(fields, communication, 'type', named('Communication - Type'));
        });
    }
};

export const parsePartiesCard = (validation: any, parties: { path: string; label: string }[], errors: ParsedCard[]) => {
    const fields: ParsedField[] = [];

    parties.forEach(({ path, label }) => parsePartyFields(get(validation, path), label, fields, path));

    if (fields.length) errors.push({ name: 'Parties', fields });
};

export const parsePlacesCard = (validation: any, places: { path: string; label: string }[], errors: ParsedCard[]) => {
    const fields: ParsedField[] = [];

    places.forEach(({ path, label }) => {
        const place = get(validation, path);
        if (!place) return;

        if (typeof place === 'string') {
            fields.push({ name: label, message: place });
            return;
        }

        handleField(fields, place, 'unlocode', `${label} - UN/LOCODE`);
        handleField(fields, place, 'location', `${label} - Location`);
        handleField(fields, place, 'address.country', `${label} - Country`);
    });

    if (fields.length) errors.push({ name: 'Places', fields });
};

export const parseTransportEquipmentCards = (validation: any, errors: ParsedCard[], prefix = '') => {
    const path = prefix ? `${prefix}.transportEquipment` : 'transportEquipment';

    parseFieldErrorsForMultipleCard(
        'Transport Equipment',
        path,
        [
            { containerIdentificationNumber: 'Container ID' },
            { containerSizeAndType: 'Container Size and Type Identification' },
            { containerPackedStatus: 'Container Packed Status' },
            { containerSupplierType: 'Container Supplier Type Code' },
            { numberOfSeals: 'Number of Seals' },
        ],
        errors,
        validation,
        path
    );

    const equipment = get(validation, path);
    if (!Array.isArray(equipment)) return;

    equipment.forEach((entry: any, index: number) => {
        parseFieldErrorsForMultipleCard(
            equipment.length > 1 ? `Seal ${index + 1}` : 'Seal',
            'seal',
            [{ identifier: 'Identifier' }],
            errors,
            entry,
            `${path}.${index}.seal`
        );
    });
};

export const parseSupportingDocumentsCard = (validation: any, errors: ParsedCard[], prefix = '') => {
    const path = prefix ? `${prefix}.supportingDocuments` : 'supportingDocuments';

    parseFieldErrorsForMultipleCard(
        'Transport Documents',
        path,
        [{ referenceNumber: 'Reference Number' }, { type: 'Type' }],
        errors,
        validation,
        path
    );
};

export const parseAdditionalSupplyChainActorCard = (validation: any, errors: ParsedCard[], prefix = '') => {
    const path = prefix ? `${prefix}.additionalSupplyChainActor` : 'additionalSupplyChainActor';

    parseFieldErrorsForMultipleCard(
        'Additional Supply Chain Actor',
        path,
        [{ role: 'Role' }, { identificationNumber: 'ID' }],
        errors,
        validation,
        path
    );
};

export const parseAdditionalInformationCard = (validation: any, errors: ParsedCard[], prefix = '') => {
    const path = prefix ? `${prefix}.additionalInformation` : 'additionalInformation';

    parseFieldErrorsForMultipleCard(
        'Additional Information',
        path,
        [{ code: 'Code' }, { text: 'Text' }],
        errors,
        validation,
        path
    );
};

export const parseSharedCards = (validation: any, errors: ParsedCard[], prefix = '') => {
    parseSupportingDocumentsCard(validation, errors, prefix);
    parseTransportEquipmentCards(validation, errors, prefix);
    parseAdditionalSupplyChainActorCard(validation, errors, prefix);
    parseAdditionalInformationCard(validation, errors, prefix);
};

export const parseMasterTransportDetailsCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForCard(
        'Transport Details',
        [
            { 'activeBorderTransportMeans.modeOfTransport': 'Mode of Transport at the Border' },
            { 'activeBorderTransportMeans.typeOfMeansOfTransport': 'Active Border Transport Means Type' },
            { 'activeBorderTransportMeans.typeOfIdentification': 'Active Border Transport Means ID Type' },
            { 'activeBorderTransportMeans.identificationNumber': 'Active Border Transport Means ID' },
            { 'activeBorderTransportMeans.nationality': 'Active Border Transport Means Nationality' },
            { 'consignmentMasterLevel.transportCharges.methodOfPayment': 'Transport Method of Payment' },
            {
                'activeBorderTransportMeans.estimatedDateAndTimeOfDeparture.dateTime':
                    'Estimated Date and Time of Departure',
            },
            {
                'activeBorderTransportMeans.actualDateAndTimeOfDeparture.dateTime': 'Actual Date and Time of Departure',
            },
            {
                'activeBorderTransportMeans.estimatedDateAndTimeOfArrival.dateTime':
                    'Estimated Date and Time of Arrival',
            },
        ],
        errors,
        validation
    );

export const parseMasterPlacesCard = (validation: any, errors: ParsedCard[]) =>
    parsePlacesCard(
        validation,
        [
            { path: 'consignmentMasterLevel.placeOfLoading', label: 'Place of Loading' },
            { path: 'consignmentMasterLevel.placeOfUnloading', label: 'Place of Unloading' },
            { path: 'consignmentMasterLevel.placeOfAcceptance', label: 'Place of Acceptance' },
            { path: 'consignmentMasterLevel.placeOfDelivery', label: 'Place of Delivery' },
        ],
        errors
    );

export const parseCustomsOfficeCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForCard(
        'Customs Office Information',
        [
            { 'customsOfficeOfFirstEntry.referenceNumber': 'Customs Office of First Entry Reference' },
            { reEntryIndicator: 'Re-entry Indicator' },
            {
                'activeBorderTransportMeans.countriesOfRoutingOfMeansOfTransport':
                    'Country of Routing of Means of Transport',
            },
            {
                'activeBorderTransportMeans.countriesOfRoutingOfMeansOfTransport.0.country':
                    'Country of Routing of Means of Transport',
            },
        ],
        errors,
        validation
    );

export const parseMasterTransportDocumentCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForCard(
        'Transport Document',
        [
            { 'consignmentMasterLevel.transportDocumentMasterLevel.documentNumber': 'Document Number' },
            { 'consignmentMasterLevel.transportDocumentMasterLevel.type': 'Type' },
        ],
        errors,
        validation
    );

export const parseHouseTransportDocumentCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForCard(
        'Transport Document',
        [
            { 'transportDocumentHouseLevel.documentNumber': 'Document Number' },
            { 'transportDocumentHouseLevel.type': 'Type' },
        ],
        errors,
        validation
    );

export const parseGoodsInformationCard = (validation: any, errors: ParsedCard[], cardName = 'Goods Information') =>
    parseFieldErrorsForCard(
        cardName,
        [
            { goodsItemNumber: 'Goods Item Number' },
            { 'commodity.commodityCode.harmonizedSystemSubHeadingCode': 'Harmonized System Sub-Heading Code' },
            { 'commodity.commodityCode.combinedNomenclatureCode': 'Combined Nomenclature Code' },
            { 'commodity.descriptionOfGoods': 'Description of Goods' },
            { 'weight.grossMass': 'Gross Mass' },
            { 'commodity.cusCode': 'CUS Code' },
        ],
        errors,
        validation
    );

export const parsePackagesCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForMultipleCard(
        'Packages',
        'packaging',
        [
            { typeOfPackages: 'Type of Packages' },
            { numberOfPackages: 'Number of Packages' },
            { shippingMarks: 'Shipping Marks' },
        ],
        errors,
        validation,
        'packaging'
    );

export const parsePassiveBorderTransportMeansCard = (validation: any, errors: ParsedCard[], prefix = '') => {
    const path = prefix ? `${prefix}.passiveBorderTransportMeans` : 'passiveBorderTransportMeans';

    parseFieldErrorsForMultipleCard(
        'Passive Border Transport Means',
        path,
        [
            { typeOfMeansOfTransport: 'Type' },
            { typeOfIdentification: 'ID Type' },
            { identificationNumber: 'ID' },
            { nationality: 'Nationality' },
        ],
        errors,
        validation,
        path
    );
};

export const parseDangerousGoodsCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForMultipleCard(
        'Dangerous Goods',
        'commodity.dangerousGoods',
        [{ unNumber: 'UN Number' }],
        errors,
        validation,
        'commodity.dangerousGoods'
    );

export const parseHouseConsignmentDetailsCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForCard(
        'Details',
        [{ 'referenceNumberUCR.referenceNumberUCR': 'Reference Number/UCR' }, { totalGrossMass: 'Total Gross Mass' }],
        errors,
        validation
    );

export const parseHouseConsignmentPlacesCard = (validation: any, errors: ParsedCard[]) =>
    parsePlacesCard(
        validation,
        [
            { path: 'placeOfAcceptance', label: 'Place of Acceptance' },
            { path: 'placeOfDelivery', label: 'Place of Delivery' },
        ],
        errors
    );

export const parseContainerCard = (validation: any, errors: ParsedCard[]) =>
    parseFieldErrorsForCard('Container', [{ containerIndicator: 'Container Indicator' }], errors, validation);

export const parseHouseConsignments = (
    validation: any,
    parseConsignmentCards: (houseConsignment: any) => ParsedCard[],
    parseItemCards: (item: any) => ParsedCard[]
): ParsedHouseConsignment[] => {
    if (!Array.isArray(validation)) return [];

    return validation
        .map((houseConsignment, index) => {
            const errors = parseConsignmentCards(houseConsignment);
            const items = parseGoodsItems(get(houseConsignment, 'goodsItem'), parseItemCards);

            parseEmptyGoodsItems(houseConsignment, 'Details', errors, items);

            return { index: index + 1, errors, items };
        })
        .filter(({ errors, items }) => errors.length > 0 || items.length > 0);
};

export const EMPTY_LEVEL_INDEX = 0;

export const emptyLevelMessage = (validation: any, path: string): string | undefined => {
    const value = get(validation, path);

    return typeof value === 'string' ? value : undefined;
};

export const parseEmptyGoodsItems = (validation: any, cardName: string, errors: ParsedCard[], items: ParsedItem[]) => {
    const message = emptyLevelMessage(validation, 'goodsItem');
    if (!message) return;

    const field: ParsedField = { name: 'Goods Items', message, path: 'goodsItem' };
    const card = errors.find(({ name }) => name === cardName);

    if (card) card.fields = [...(card.fields ?? []), field];
    else errors.push({ name: cardName, fields: [field] });

    items.push({ index: EMPTY_LEVEL_INDEX, errors: [] });
};

export const parseGoodsItems = (validation: any, parseCards: (item: any) => ParsedCard[]): ParsedItem[] => {
    if (!Array.isArray(validation)) return [];

    return validation
        .map((item, index) => ({ index: index + 1, errors: parseCards(item) }))
        .filter(({ errors }) => errors.length > 0);
};

export const parseEmptyLevels = (
    validation: any,
    masterDetails: ParsedCard[],
    levels: { path: string; name: string }[]
) => {
    const fields: ParsedField[] = [];

    levels.forEach(({ path, name }) => {
        const message = emptyLevelMessage(validation, path);

        if (message) fields.push({ name, message, path });
    });

    if (!fields.length) return;

    const masterDetailsCard = masterDetails.find(({ name }) => name === 'Master Details');

    if (masterDetailsCard) masterDetailsCard.fields = [...(masterDetailsCard.fields ?? []), ...fields];
    else masterDetails.push({ name: 'Master Details', fields });
};
